import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Pencil, Calendar, MapPin, Link as LinkIcon, CheckCircle2, Heart, Images, Bell, ArrowUp, X, Navigation, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors, themeOf } from "@/lib/theme";
import { longDate, formatTime, commentTime } from "@/lib/date";
import { Avatar, Textarea, Spinner } from "@/components/peeps/ui";
import { EventFormSheet } from "@/components/peeps/EventFormSheet";
import { LiveLocationMap } from "@/components/peeps/LiveLocationMap";
import { CommentThread } from "@/components/peeps/CommentThread";
import { HomeButton } from "@/components/peeps/HomeButton";
import { FriendsGoing } from "@/components/peeps/FriendsGoing";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prox, setProx] = useState(null);

  const loadProx = useCallback(async () => {
    try {
      setProx(await api.getEventProximity(String(id)));
    } catch {}
  }, [id]);

  const load = useCallback(async () => {
    try {
      setEv(await api.getEvent(String(id)));
    } catch (e) {
      toast(e.message || "Couldn't load plan");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProx(); }, [loadProx]);

  const toggleProx = async () => {
    if (!prox) return;
    const next = !prox.is_sharing;
    setProx({ ...prox, is_sharing: next });
    try {
      setProx(await api.setEventProximity(String(id), next));
    } catch (e) {
      toast(e.message || "Couldn't update");
      loadProx();
    }
  };

  const th = themeOf(ev?.group?.theme);
  const canBlast = true;

  const toggleVote = async () => {
    setEv((p) => ({ ...p, voted: !p.voted, vote_count: p.vote_count + (p.voted ? -1 : 1) }));
    try { await api.vote(String(id)); } catch { load(); }
  };

  const toggleGoing = async () => {
    const was = ev.im_going;
    setEv((p) => ({
      ...p,
      im_going: !was,
      going_count: p.going_count + (was ? -1 : 1),
      goers: was ? p.goers.filter((g) => g.id !== user?.id) : [...p.goers, { id: user?.id, display_name: user?.display_name, profile_photo_url: user?.profile_photo_url }],
    }));
    try { await api.rsvp(String(id)); } catch { load(); }
  };

  const notifyGroup = async () => {
    try {
      const res = await api.blast(String(id));
      toast(res.count > 0 ? `Notified ${res.count} member${res.count === 1 ? "" : "s"}` : "No one else to notify yet");
    } catch (e) { toast(e.message || "Couldn't notify"); }
  };

  const submitEdit = async (fields) => {
    setSubmitting(true);
    try {
      await api.updateEvent(String(id), fields);
      setEditing(false);
      toast("Plan updated");
      await load();
    } catch (e) { toast(e.message || "Couldn't save"); }
    finally { setSubmitting(false); }
  };

  const deleteEvent = async () => {
    try {
      await api.deleteEvent(String(id));
      toast("Plan deleted");
      navigate(-1);
    } catch (e) { toast(e.message || "Couldn't delete"); }
  };

  if (loading || !ev) {
    return <div className="flex justify-center items-center min-h-screen" style={{ background: colors.surface }}><Spinner /></div>;
  }

  return (
    <div className="min-h-screen max-w-2xl mx-auto flex flex-col" style={{ background: colors.surface }}>
      <div className="flex items-center px-3 pt-4 pb-3" style={{ background: th.solid }}>
        <button data-testid="event-back" onClick={() => navigate(-1)} className="rounded-full p-1.5" style={{ background: "rgba(21,22,30,0.2)" }}><ChevronLeft size={22} color={th.on} /></button>
        <div className="ml-2"><HomeButton size={32} bg="rgba(21,22,30,0.2)" /></div>
        <div className="flex-1" />
        {ev.can_edit && (
          <button data-testid="event-edit-button" onClick={() => setEditing(true)} className="rounded-full p-1.5" style={{ background: "rgba(21,22,30,0.2)" }}><Pencil size={17} color={th.on} /></button>
        )}
      </div>

      <div className="flex-1 p-4 pb-40">
        <h1 className="font-display font-bold text-2xl mb-3" style={{ color: colors.onSurface }}>{ev.title}</h1>

        <div className="flex items-center gap-2 mb-2">
          <Calendar size={16} color={th.solid} />
          <span className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurfaceSecondary }}>{ev.end_date && ev.end_date > ev.date ? `${longDate(ev.date)} – ${longDate(ev.end_date)}` : longDate(ev.date)} · {formatTime(ev.time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}</span>
        </div>
        {!!ev.location && (
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} color={th.solid} />
            <span className="font-display font-medium text-[14.5px]" style={{ color: colors.onSurfaceSecondary }}>{ev.location}</span>
          </div>
        )}
        {!!ev.link && (
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon size={16} color={th.solid} />
            <a href={ev.link} target="_blank" rel="noreferrer" className="font-display font-medium text-[14.5px] truncate" style={{ color: th.solid }}>{ev.link}</a>
          </div>
        )}
        {!!ev.details && <p className="font-display text-sm mt-2 leading-relaxed" style={{ color: colors.onSurfaceTertiary }}>{ev.details}</p>}
        <p className="font-display text-[12.5px] mt-2" style={{ color: colors.faint }}>
          {ev.source === "chat_import" ? (ev.source_label || "Added from the group chat") : `Added by ${ev.creator?.display_name || "someone"}`}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <button data-testid="detail-going-button" onClick={toggleGoing} className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3"
            style={{ background: ev.im_going ? colors.success : colors.surfaceSecondary, border: `1px solid ${ev.im_going ? "transparent" : colors.border}` }}>
            <CheckCircle2 size={18} color={ev.im_going ? colors.onSuccess : colors.onSurface} fill={ev.im_going ? colors.onSuccess : "none"} />
            <span className="font-display font-bold text-[14.5px]" style={{ color: ev.im_going ? colors.onSuccess : colors.onSurface }}>{ev.im_going ? "I'm going" : "I'm in?"}</span>
          </button>
          <button data-testid="detail-vote-button" onClick={toggleVote} className="flex items-center gap-1.5 rounded-xl py-3 px-4"
            style={{ background: ev.voted ? th.solid : colors.surfaceSecondary, border: `1px solid ${ev.voted ? "transparent" : colors.border}` }}>
            <Heart size={17} color={ev.voted ? th.on : colors.onSurface} fill={ev.voted ? th.on : "none"} />
            <span className="font-mono2 font-medium text-[14px]" style={{ color: ev.voted ? th.on : colors.onSurface }}>{ev.vote_count}</span>
          </button>
          <button data-testid="event-gallery-button" onClick={() => navigate(`/event/${id}/gallery`)} className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <Images size={18} color={colors.onSurfaceSecondary} />
          </button>
          {canBlast && (
            <button data-testid="detail-blast-button" onClick={notifyGroup} className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <Bell size={18} color={colors.onSurfaceSecondary} />
            </button>
          )}
        </div>

        {/* Proximity — minimal, single toggle (full control lives in Profile) */}
        {prox && (
          <div data-testid="event-proximity-card" className="mt-4 rounded-xl p-3.5" style={{ background: prox.live ? colors.success + "1A" : colors.surfaceSecondary, border: `1px solid ${prox.live ? colors.success : colors.border}` }}>
            <div className="flex items-center gap-3">
              <Navigation size={18} color={prox.is_sharing ? (prox.window_active ? colors.success : th.solid) : colors.onSurfaceTertiary} />
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-[14px]" style={{ color: colors.onSurface }}>Share my location for this event</p>
                <p className="font-display text-[12px]" style={{ color: colors.onSurfaceTertiary }}>
                  {!prox.is_sharing
                    ? "Off for this event"
                    : !prox.is_going
                      ? "RSVP Going to share at the event"
                      : prox.window_active
                        ? `Sharing now · within ${prox.radius_threshold >= 1000 ? prox.radius_threshold / 1000 + " km" : prox.radius_threshold + " m"}`
                        : "On — activates around event time"}
                </p>
              </div>
              <button data-testid="event-proximity-toggle" onClick={toggleProx} className="relative inline-flex items-center rounded-full transition-colors shrink-0" style={{ width: 42, height: 24, background: prox.is_sharing ? (prox.window_active ? colors.success : th.solid) : colors.surfaceTertiary }}>
                <span className="absolute rounded-full bg-white transition-transform" style={{ width: 18, height: 18, top: 3, left: 3, transform: prox.is_sharing ? "translateX(18px)" : "translateX(0)" }} />
              </button>
            </div>
            {prox.live && (
              <div className="flex items-center gap-1.5 mt-2.5 rounded-lg px-2.5 py-1.5" style={{ background: colors.success + "22" }}>
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.success }} />
                <span className="font-display font-medium text-[11.5px]" style={{ color: colors.success }}>Location sharing is live</span>
                <span className="flex-1" />
                <button data-testid="event-proximity-revoke" onClick={toggleProx} className="font-display font-bold text-[11.5px]" style={{ color: colors.success }}>Stop now</button>
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <span className="font-display text-[11.5px]" style={{ color: colors.faint }}>
                {prox.is_sharing ? `${prox.sharing_count} other${prox.sharing_count === 1 ? "" : "s"} sharing` : "Turn on to see who's sharing"}
              </span>
              <button data-testid="proximity-info-link" onClick={() => navigate("/profile")} className="flex items-center gap-1 font-display text-[11.5px]" style={{ color: th.solid }}>
                <Info size={12} /> How this works
              </button>
            </div>

            {prox.is_sharing && prox.window_active && prox.is_going && (
              <LiveLocationMap eventId={String(id)} active={prox.is_sharing && prox.window_active && prox.is_going} themeColor={th.solid} />
            )}
          </div>
        )}

        {/* Going list */}
        <div className="mt-6">
          <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-3" style={{ color: colors.muted }}>Going ({ev.going_count})</p>
          {(ev.friends_going_count ?? 0) > 0 && (
            <div className="mb-3 rounded-xl p-2.5" style={{ background: colors.success + "14", border: `1px solid ${colors.success}33` }}>
              <FriendsGoing friends={ev.friends_going} size={24} max={5} />
            </div>
          )}
          {ev.goers.length === 0 ? (
            <p className="font-display text-[13.5px]" style={{ color: colors.faint }}>No one's confirmed yet. Be the first to say you're in.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {ev.goers.map((g) => (
                <div key={g.id} className="flex flex-col items-center gap-1 w-14">
                  <Avatar name={g.display_name} uri={g.profile_photo_url} size={30} />
                  <span className="font-display text-[11px] text-center truncate w-full" style={{ color: colors.onSurfaceTertiary }}>{g.id === user?.id ? "You" : g.display_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="mt-6 pb-10">
          <CommentThread parentType="event" parentId={String(id)} accent={th.solid} onColor={th.on} onCountChange={(n) => setEv((p) => (p ? { ...p, comment_count: n } : p))} />
        </div>
      </div>

      {editing && (
        <EventFormSheet visible mode="edit" initial={ev} googleSynced={false} submitting={submitting} onClose={() => setEditing(false)} onSubmit={submitEdit} onDelete={deleteEvent} />
      )}
    </div>
  );
}
